// Growem: strength quantification & aggregation.
//
// WHY THIS EXISTS (from the expert audit + adversarial verification, 2026-07-27):
// The old "Fuerza" chart used a static hardcoded array (one exercise per group), a session-INDEX
// x-axis (not calendar time), a weight-only metric that can't represent reps-only lifts, and an
// average-of-percentages aggregate (the biased "Carli index"). This module replaces all of that,
// computed from the REAL per-set logs (day_state.log).
//
// RECALIBRATED 2026-08 (PhD panel + 10-auditor review, docs/STRENGTH_INDEX_ACADEMIC_REVIEW.md):
// the architecture (Jevons chained index, top set, honesty gates) was validated and KEPT; six
// application defects were fixed in ONE recalibration (S1 RIR imputation, S4 chained overview,
// S6 staleness window, S7 alias identity, S8 reliable top set, plus the trend baseline):
//
// THE MODEL:
//  1. Per SET, weight×reps(×RIR) → estimated 1RM (e1RM) via Epley, RIR-adjusted (Epley 1985;
//     LeSuer et al. JSCR 1997; Zourdos et al. 2016). A set with NO RIR logged is imputed with the
//     user's typical effort (median per exercise n>=2 → global n>=5 → prior 2, hypertrophy RIR 1-3
//     per Helms 2016; Halperin 2022 meta: unset RIR is NOT failure), clamped to [0,4]. Assuming
//     failure (the old `rir ?? 0`) made sessions with and without RIR incomparable (+5% spurious
//     at r=10, 71% of the trend gate). The estimator is CAUSAL (Fase 4 SHOULD_FIX): the median of
//     a point dated D only ever reads RIRs logged on or before D, so logging a new session can no
//     longer rewrite a chart the user already saw (see buildRirHat for the full rationale).
//  2. Per EXERCISE per SESSION → the TOP set. SELECTION key is v0 = e1rm(w, r, 0) (independent of
//     RIR adoption); the stored VALUE uses the RIR-adjusted e1RM. Sets inside Epley's validity
//     range (OBSERVED reps <= 12; an imputed RIR never gates, Fase 4 MF0) are preferred: if any
//     reliable set exists the top comes from
//     them; otherwise the global max wins and the point is born lowConf (Reynolds 2006: R² decays
//     5RM→20RM). Exercise identity goes through canonicalName() (plan.ts): V1→V2 pure renames
//     continue ONE series (CPI comparable replacement); user bundles are NEVER canonized (gate D5).
//  3. Per MUSCLE GROUP → a CHAINED, EQUAL-WEIGHT GEOMETRIC (Jevons) index rebased to 100.
//     Chain-linked over members present in BOTH consecutive periods, so a newly-added lift enters
//     with a zero splice jump (CPI basket update). A member not retested votes ratio 1 via LOCF;
//     that dilution is the CORRECT unbiased n/m shrinkage under A/B rotation (validated against
//     the refuted "matched-only" family: it double-counts growth, +21% where +10% happened), but
//     only while FRESH: after STALE_DAYS without a real observation the member leaves the basket
//     (Lachin 2016: indefinite LOCF is invalid; CPI: disappeared items are retired) and re-enters
//     later through the zero splice. Each step's log-ratio is clamped to ±ln(1.30) (Szulc 1983:
//     outliers hurt chained indices most); a clamped step is flagged `provisional`.
//  4. "Todas" overview = a SECOND-LEVEL CHAINED index over the group indices (mirror of #3), NOT
//     a geometric mean of levels: a debuting group enters with zero splice and can no longer drag
//     the general down (the 2026-08-01 incident: glúteo debuted at 100 and the general showed 115
//     while the user actually improved). Group ratios arrive already clamped; re-clamping here
//     would be dead code (mean of |ln|<=cap is <=cap).
//  5. Trend badge: Theil-Sen slope in log space (Sen 1968, robust to a single wild point) over the
//     last TREND_WINDOW_DAYS anchored to the LAST data point (never the wall clock: the whole
//     module is clock-free and memoizable on [days]); gate ±TREND_GATE unchanged. With fewer than
//     MIN_TREND_POINTS in the window it falls back to the v1 whole-series read.
// Reps-only/bodyweight lifts stay a separate reps series, NOT fed a fake weight into the index.

import type { DayState, SetLog } from '../types'
import { planForDate, groupOf, isRepsOnly, canonicalName, GROUP_LABEL, type MuscleGroup } from '../data/plan'
import { parseDay } from '../domain/dates'
import { parseSetKey } from '../domain/rules'
import { ASSISTANCE_EXERCISES } from '../domain/strengthStandards'

/** Estimated 1RM (Epley), RIR-adjusted. r_eff = reps + RIR ≈ reps-to-failure. */
export function e1rm(weight: number, reps: number, rir = 0): number {
  return weight * (1 + (reps + rir) / 30)
}

/** Effective-reps threshold above which e1RM is treated as low-confidence (audit: valid ≤~10-12). */
const REPS_CONF_MAX = 12
/** Net change (fraction) that must be exceeded before we say "mejorando/bajando": the ±noise gate
 *  (audit §4/§6: ~5-8% e1RM typical error; use ~7% so 2-3 noisy points never fake a trend). */
export const TREND_GATE = 0.07
/** Minimum sessions before a series is allowed a trend line / direction word at all. */
export const MIN_TREND_POINTS = 3
/** Days without a REAL observation after which a member (exercise, or group in the overview) leaves
 *  the basket. Evaluated against the STEP date, never the wall clock (design D1). 45d ≈ 6 semanas;
 *  defensible range 35-60 (academic review, mustDo 2). */
export const STALE_DAYS = 45
/** Per-step |log-ratio| cap (±ln 1.30). A clamped step is flagged `provisional` (review, shouldDo 5). */
export const STEP_CLAMP_LN = Math.log(1.30)
/** Trend window (days) anchored at the series' LAST point (clock-free, design D1). */
export const TREND_WINDOW_DAYS = 56
/** Hypertrophy prior for imputed RIR when the user has no usable RIR history (review, mustDo 3). */
export const RIR_PRIOR = 2
/** Imputed/effective RIR is clamped to [0, RIR_MAX]. */
const RIR_MAX = 4

function median(xs: number[]): number {
  if (xs.length === 0) return 0
  const s = [...xs].sort((a, b) => a - b)
  const m = Math.floor(s.length / 2)
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2
}

/** Whole days between two local ISO dates (a - b). */
function diffDays(aIso: string, bIso: string): number {
  return Math.round((parseDay(aIso).getTime() - parseDay(bIso).getTime()) / 86400000)
}

/**
 * Fallback: extract weight(kg) and reps from a free-text note. Sebastian's early history stored
 * marks in `notes` (e.g. "120 kg × 10 · PR", "41 kg x 12") rather than the structured `log`; this
 * recovers a point WITHOUT fabricating: only when both a "N kg" and an "× N / x N / N reps" appear.
 */
function parseNote(note: string | undefined): { w?: number; r?: number } {
  if (!note) return {}
  const w = note.replace(',', '.').match(/(\d+(?:\.\d+)?)\s*kg/i)?.[1]
  const r = note.match(/[×x]\s*(\d+)/)?.[1] ?? note.match(/(\d+)\s*reps/i)?.[1]
  return { w: w ? Number(w) : undefined, r: r ? Number(r) : undefined }
}

// ---------- shared per-set resolution (ONE code path for chart + history, audit Fase 1B) ----------

interface ResolvedSet {
  date: string
  key: string          // raw set-key, part of the stable total order
  name: string         // canonical series name (alias applied; second defense keeps logged name)
  group: MuscleGroup
  repsOnly: boolean
  assist: boolean
  w?: number
  r: number
  rir?: number         // RAW logged RIR (undefined = not logged; 0 is DATA, not absence)
}

/**
 * Resolve every completed set of every day to its series identity + raw numbers, in a STABLE total
 * order (date, then key) so downstream folds are deterministic regardless of Object key order.
 * Resolution mirrors the pre-recalibration logic exactly (variant maps, superset children via
 * pair[c], parseNote fallback, unmapped names skipped) plus canonicalName() with a second defense:
 * if a curated canon does not map to a group, the logged name is kept; a bad table entry must
 * never make sets disappear.
 */
function resolveSets(days: Record<string, DayState>): ResolvedSet[] {
  const out: ResolvedSet[] = []
  for (const day of Object.values(days)) {
    const plan = planForDate(day.day) // date-versioned: <07-28 -> V1, >=07-28 -> V2 (keeps history intact)
    for (const key of Object.keys(day.done)) {
      if (!/^\d/.test(key)) continue // skip warm-up / cool-down keys (prep-*/post-*)
      const { d, e, c } = parseSetKey(key)
      const ex = plan[d]?.ex[e]
      if (!ex) continue
      // Variant-aware: a per-day exercise swap attributes the sets to the VARIANT name (that day's map).
      const logged = c === -1 ? (day.variants?.[`${d}-${e}`] ?? ex.n) : ex.pair?.[c]?.n
      if (!logged) continue
      const canon = canonicalName(logged)
      const name = groupOf(canon) ? canon : logged // second defense: a bad alias entry is inert
      const group = groupOf(name)
      if (!group) continue // unmapped (Farmer carry, Plancha: seconds live in r) -> no strength point
      // Prefer the structured log; fall back to parsing the note (early history lived in notes).
      const set: SetLog = day.log[key] ?? {}
      let w = set.w, reps = set.r
      if (w == null || reps == null) { const p = parseNote(day.notes[key]); w = w ?? p.w; reps = reps ?? p.r }
      if (reps == null) continue
      out.push({ date: day.day, key, name, group, repsOnly: isRepsOnly(name), assist: ASSISTANCE_EXERCISES.has(name), w, r: reps, rir: set.rir })
    }
  }
  out.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : a.key < b.key ? -1 : a.key > b.key ? 1 : 0))
  return out
}

/**
 * PREFIX-MEDIAN TABLE of the logged RIRs of one series (or of the whole history).
 *
 * `dates` are the DISTINCT days on which that stream got at least one RIR logged, ascending;
 * `counts[i]` / `medians[i]` are how many RIRs had been logged up to and including `dates[i]` and
 * their median. That is exactly "what we knew on day X", precomputed once, so answering a query is
 * one binary search instead of re-sorting the history (Fase 4 SHOULD_FIX de rendimiento).
 */
interface RirTable { dates: string[]; counts: number[]; medians: number[] }

/** Estimador de esfuerzo tipico para los sets SIN RIR anotado, evaluado A UNA FECHA. */
type RirHat = (name: string, date: string) => number

/**
 * Build the prefix-median table from a date-ASCENDING stream of observations. The running median
 * comes from an insertion-sorted array (binary search + splice): every value is inserted once, so
 * the whole table costs one pass instead of one sort per query. The splice is a memmove over a
 * packed numeric array, and this stream is only "the sets the user annotated with a RIR", so even
 * a decade of training is a few thousand elements: cheaper than the single full sort it replaces.
 */
function mkRirTable(dates: readonly string[], rirs: readonly number[]): RirTable {
  const t: RirTable = { dates: [], counts: [], medians: [] }
  const sorted: number[] = []
  for (let i = 0; i < rirs.length; i++) {
    let lo = 0, hi = sorted.length
    while (lo < hi) { const mid = (lo + hi) >> 1; if (sorted[mid] < rirs[i]) lo = mid + 1; else hi = mid }
    sorted.splice(lo, 0, rirs[i])
    // Cerrar el tramo SOLO al cambiar de dia: todas las anotaciones de una misma fecha entran
    // juntas, o sea una sesion se ve a si misma pero jamas a las que vinieron despues.
    if (i + 1 === rirs.length || dates[i + 1] !== dates[i]) {
      const m = sorted.length >> 1
      t.dates.push(dates[i])
      t.counts.push(sorted.length)
      t.medians.push(sorted.length % 2 ? sorted[m] : (sorted[m - 1] + sorted[m]) / 2)
    }
  }
  return t
}

/** What the table knew at `date`: how many observations, and their median (n = 0 if none yet). */
function rirKnownAt(t: RirTable, date: string): { n: number; med: number } {
  let lo = 0, hi = t.dates.length
  while (lo < hi) { const mid = (lo + hi) >> 1; if (t.dates[mid] <= date) lo = mid + 1; else hi = mid }
  return lo === 0 ? { n: 0, med: 0 } : { n: t.counts[lo - 1], med: t.medians[lo - 1] }
}

/**
 * Typical-effort estimator for sets with no RIR logged: median per exercise (n>=2) -> global
 * (n>=5) -> RIR_PRIOR. Built ONCE per build over the sets that DID log RIR (weighted lifts only).
 *
 * CAUSAL POR DISENO (Fase 4 SHOULD_FIX). The estimator takes the point's DATE and only ever reads
 * observations dated on or before it. Rationale: the previous version took the median over the
 * WHOLE history, so logging a session today silently rewrote points the user had already seen.
 * Measured on the real fixture: one extra day (2026-08-03, 10 sets at RIR 4) moved 11 of the
 * general index's 13 dates, from 2026-07-17 onwards, by up to 0.41 points, on dates whose own data
 * had not changed by one gram; the same experiment under the causal rule moves 0 of 13. The shift
 * does NOT cancel out in a ratio index, because it only moves the IMPUTED points and leaves the
 * explicitly annotated ones alone, so it distorts mixed steps. Under the causal rule the past is
 * frozen: a new session can only ever change points dated on or after itself. The price is that
 * the earliest points use the prior (2) instead of a median that did not exist yet, which is the
 * honest reading anyway (we cannot know in June what the user's typical effort will be in August).
 *
 * The same-DAY observations DO count for that day's own points: the unit of causality is the day,
 * not the individual set, so a set annotated in the morning informs an un-annotated set logged in
 * the same session (and the published value stops depending on set order within the day).
 *
 * PERFORMANCE (same SHOULD_FIX): the medians are precomputed per series and once globally, so a
 * query is two binary searches. The previous closure re-sorted a copy of the history on EVERY
 * un-annotated set.
 */
function buildRirHat(sets: ResolvedSet[]): RirHat {
  const obsByName = new Map<string, { dates: string[]; rirs: number[] }>()
  const allDates: string[] = [], allRirs: number[] = []
  // `sets` comes out of resolveSets already sorted by (date, key), so pushing in order keeps every
  // stream ascending by date: that is exactly what makes the prefix tables legal.
  for (const s of sets) {
    if (s.rir == null || s.repsOnly || s.w == null) continue
    let o = obsByName.get(s.name)
    if (!o) { o = { dates: [], rirs: [] }; obsByName.set(s.name, o) }
    o.dates.push(s.date); o.rirs.push(s.rir)
    allDates.push(s.date); allRirs.push(s.rir)
  }
  const byName = new Map<string, RirTable>()
  for (const [name, o] of obsByName) byName.set(name, mkRirTable(o.dates, o.rirs))
  const all = mkRirTable(allDates, allRirs)

  return (name, date) => {
    const t = byName.get(name)
    const per = t ? rirKnownAt(t, date) : { n: 0, med: 0 }
    let hat: number
    if (per.n >= 2) hat = per.med
    else { const g = rirKnownAt(all, date); hat = g.n >= 5 ? g.med : RIR_PRIOR }
    return Math.min(RIR_MAX, Math.max(0, hat))
  }
}

const rirEffOf = (s: ResolvedSet, rirHat: RirHat): number =>
  Math.min(RIR_MAX, Math.max(0, s.rir ?? rirHat(s.name, s.date)))

/** Deterministic top-set comparator on the v0 key (Fase 4 SHOULD_FIX): on a v0 TIE the set WITH a
 *  logged RIR beats the imputed one (data beats guess: the published value and the raw TopSet.rir
 *  must not depend on which set the user happened to annotate); a remaining tie keeps the first in
 *  the stable (date, key) order. Never tie-break by the RIR-adjusted value: that would re-open the
 *  adoption bias the panel rejected (dual v0/vR mechanism). */
function better(cand: { v0: number; rir?: number }, inc?: { v0: number; rir?: number }): boolean {
  if (!inc) return true
  if (cand.v0 !== inc.v0) return cand.v0 > inc.v0
  return inc.rir == null && cand.rir != null
}

// ---------- per-exercise series ----------

export interface ExercisePoint {
  date: string   // local ISO day
  t: number      // epoch ms (calendar-time x-axis)
  value: number  // e1RM (kg, RIR-adjusted) for weighted lifts, or top-set reps for reps-only
  nSets: number  // completed sets that day (context)
  lowConf: boolean
  rirImputed?: boolean // true when the top set had no RIR logged (value used the imputed typical effort)
}
export interface ExerciseSeries {
  name: string
  group: MuscleGroup
  repsOnly: boolean
  unit: 'kg' | 'reps'
  base: number   // baseline value used to rebase to 100 (median of first reliable sessions)
  points: ExercisePoint[]
}

/**
 * Build a per-exercise time series from the logged days. Only COMPLETED sets count; the
 * per-session value is the TOP set: selected by v0 = e1rm(w, r, 0) among RELIABLE sets
 * (OBSERVED reps <= REPS_CONF_MAX; the imputed RIR never gates, Fase 4 MF0) when any exists,
 * else among all (point born lowConf); the
 * stored value is the RIR-adjusted e1RM of the winner. Reps-only lifts rank by reps. ASSISTANCE
 * lifts are excluded from the chart only (a decreasing-assistance series would graph as
 * regression); they stay fully tracked in buildHistoryIndex for targets/progression.
 */
export function buildExerciseSeries(days: Record<string, DayState>): ExerciseSeries[] {
  const sets = resolveSets(days)
  const rirHat = buildRirHat(sets)
  interface DayAcc { nSets: number; bestRel?: { v0: number; value: number; rir?: number }; bestAny?: { v0: number; value: number; rir?: number }; bestReps?: number }
  const acc = new Map<string, Map<string, DayAcc>>()
  const meta = new Map<string, { group: MuscleGroup; repsOnly: boolean }>()

  for (const s of sets) {
    if (s.assist) continue
    if (!s.repsOnly && s.w == null) continue // a weighted lift with no load carries no strength signal
    meta.set(s.name, { group: s.group, repsOnly: s.repsOnly })
    let byDate = acc.get(s.name)
    if (!byDate) { byDate = new Map(); acc.set(s.name, byDate) }
    const cur = byDate.get(s.date) ?? { nSets: 0 }
    cur.nSets += 1
    if (s.repsOnly) {
      cur.bestReps = Math.max(cur.bestReps ?? -Infinity, s.r)
    } else {
      const rirEff = rirEffOf(s, rirHat)
      const cand = { v0: e1rm(s.w!, s.r, 0), value: e1rm(s.w!, s.r, rirEff), rir: s.rir }
      // Eligibility uses OBSERVED reps only (Fase 4 MUST_FIX): an imputed RIR is a guess and must
      // never expel the user's real top set from the pool (with the modal 12-rep set, 12+hat(2)=14
      // was crowning back-off sets and flagging 72% of real points lowConf). rirEff stays in the
      // VALUE only. The S8 motivator holds: a 15-rep set still loses to a valid 8-rep one (15>12).
      if (s.r <= REPS_CONF_MAX && better(cand, cur.bestRel)) cur.bestRel = cand
      if (better(cand, cur.bestAny)) cur.bestAny = cand
    }
    byDate.set(s.date, cur)
  }

  const out: ExerciseSeries[] = []
  for (const [name, byDate] of acc) {
    const m = meta.get(name)!
    const points: ExercisePoint[] = [...byDate.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, v]) => {
        if (m.repsOnly) return { date, t: parseDay(date).getTime(), value: v.bestReps!, nSets: v.nSets, lowConf: false }
        const chosen = v.bestRel ?? v.bestAny!
        // lowConf: no set inside Epley's validity range that day, OR the winner logged an extreme
        // explicit RIR (> 3). An IMPUTED rirEff never triggers the explicit-RIR arm (design D2).
        const lowConf = v.bestRel == null || (chosen.rir != null && chosen.rir > 3)
        return { date, t: parseDay(date).getTime(), value: chosen.value, nSets: v.nSets, lowConf, rirImputed: chosen.rir == null }
      })
    // Base = median of the first up-to-3 RELIABLE sessions (so one fluke/deload day doesn't poison
    // the whole rebased series); fall back to the first sessions if none are reliable.
    const reliable = points.filter((p) => !p.lowConf).slice(0, 3)
    const baseVals = (reliable.length ? reliable : points.slice(0, 3)).map((p) => p.value)
    out.push({ name, group: m.group, repsOnly: m.repsOnly, unit: m.repsOnly ? 'reps' : 'kg', base: median(baseVals) || 1, points })
  }
  return out
}

// ---------- per-group chained geometric index ----------

export interface IndexPoint {
  date: string
  t: number
  value: number      // 100 = baseline
  obs?: number       // voters in the step that produced this point (0 on the first point)
  provisional?: boolean // true when the step's log-ratio was clamped to ±STEP_CLAMP_LN
  lowConf?: boolean  // true when a voter's REAL observation on this date was low-confidence
}
export interface GroupIndex {
  group: MuscleGroup
  label: string
  points: IndexPoint[]
  exercises: ExerciseSeries[] // weighted members (for the per-exercise detail lines)
}

/**
 * MONOTONE CURSORS over the members of a chained index (Fase 4 SHOULD_FIX, rendimiento).
 *
 * WHY: the chaining loop walks the step dates in ASCENDING order and every member's points are
 * ascending too, so the three questions each step asks about each member (what was its last real
 * observation at `prev`? what is its LOCF value at `prev` and at `cur`? did it train exactly on
 * `cur`?) all move forward and NEVER backwards. The previous version re-scanned the member's
 * points from index 0 on every step (locf twice + lastObsDate + a `find` with no early exit at
 * all), i.e. O(dates x members x points) per group, and the model is rebuilt on every keystroke of
 * the Sesión screen. One cursor per member that only ever advances answers all three questions in
 * O(1) amortized, so the whole build is O(total points): measured on a synthetic 3-year fixture
 * (468 days, 7020 sets, 22 series, 8 groups, 390 dates in the general), buildStrengthModel drops
 * from 40.8 ms to 21.5 ms (median of 7 runs, same machine, same fixture).
 *
 * This is a PURE speed change: the cursor lands on exactly the point the linear scans used to
 * return, so every number is bit-identical. Verified beyond the golden test by dumping the whole
 * model (general + every group + every series, full double precision) before and after: zero diff.
 *
 * `at[m]` = index of the LAST point of member m dated <= the date the cursor was last advanced to,
 * or -1 while the member has not debuted yet (which is precisely "does not vote": zero splice).
 */
interface Cursors { at: number[]; advance: (m: number, date: string) => void }
function mkCursors(members: ReadonlyArray<{ points: ReadonlyArray<{ date: string }> }>, firstDate: string): Cursors {
  const at = members.map(() => -1)
  const advance = (m: number, date: string) => {
    const pts = members[m].points
    while (at[m] + 1 < pts.length && pts[at[m] + 1].date <= date) at[m]++
  }
  for (let m = 0; m < members.length; m++) advance(m, firstDate)
  return { at, advance }
}

/** Whole days between two epoch-ms LOCAL midnights (same rounding as diffDays, DST-proof). */
const daysBetween = (aT: number, bT: number): number => Math.round((aT - bT) / 86400000)

/**
 * Chained, equal-weight GEOMETRIC index per muscle group, rebased to 100 at the group's first
 * session. Reps-only members are excluded (no e1RM). Each step moves the level by the geometric
 * mean of the RATIOS of the members present in BOTH periods (chain-linking: a new lift splices in
 * with no jump). A fresh member not retested votes ratio 1 via LOCF (correct n/m shrinkage); a
 * member whose last real observation is older than STALE_DAYS at the step date leaves the basket
 * (and re-enters later through the same zero splice). The step's log-ratio is clamped to
 * ±STEP_CLAMP_LN (flagged `provisional`).
 */
export function buildGroupIndices(series: ExerciseSeries[]): GroupIndex[] {
  const byGroup = new Map<MuscleGroup, ExerciseSeries[]>()
  for (const s of series) {
    if (s.repsOnly) continue // reps-only lifts never vote in the strength index
    const arr = byGroup.get(s.group) ?? []
    arr.push(s)
    byGroup.set(s.group, arr)
  }

  const out: GroupIndex[] = []
  for (const [group, exs] of byGroup) {
    const dates = [...new Set(exs.flatMap((s) => s.points.map((p) => p.date)))].sort()
    if (dates.length === 0) continue
    const ts = dates.map((d) => parseDay(d).getTime()) // una sola vez: el paso ya no re-parsea ISOs

    const firstLowConf = exs.some((s) => s.points.some((p) => p.date === dates[0] && p.lowConf))
    const points: IndexPoint[] = [{ date: dates[0], t: ts[0], value: 100, obs: 0, provisional: false, lowConf: firstLowConf }]
    const cursors = mkCursors(exs, dates[0]) // arranca posicionado en dates[0]
    let level = 100
    for (let i = 1; i < dates.length; i++) {
      const date = dates[i]
      let sumLn = 0, n = 0, lowConf = false
      for (let m = 0; m < exs.length; m++) {
        const pts = exs[m].points
        const iPrev = cursors.at[m] // quedo en dates[i-1] al final de la vuelta anterior
        cursors.advance(m, date)
        const iCur = cursors.at[m]
        if (iPrev < 0) continue // aun no debuta: no vota (empalme a salto cero)
        // Vigencia: la ultima observacion REAL a fecha `prev` debe estar a <= STALE_DAYS del paso.
        if (daysBetween(ts[i], pts[iPrev].t) > STALE_DAYS) continue // stale member: out of the basket
        const vp = pts[iPrev].value, vc = pts[iCur].value // LOCF: iCur >= iPrev >= 0, siempre hay valor
        if (vp > 0 && vc > 0) {
          sumLn += Math.log(vc / vp); n++
          // Las fechas de una serie son unicas, asi que el punto REAL del dia (si entreno) solo
          // puede ser el que el cursor acaba de dejar bajo la lupa.
          if (pts[iCur].date === date && pts[iCur].lowConf) lowConf = true
        }
      }
      const stepLn = n > 0 ? sumLn / n : 0 // no eligible member → index holds
      const clamped = Math.min(STEP_CLAMP_LN, Math.max(-STEP_CLAMP_LN, stepLn))
      level *= Math.exp(clamped)
      points.push({ date, t: ts[i], value: level, obs: n, provisional: clamped !== stepLn, lowConf })
    }
    // Fresh copy (Fase 4 / enmienda MF19): the model is memoized and shared; publishing the
    // internal array invites an in-place sort in a consumer to silently reorder the frozen model.
    out.push({ group, label: GROUP_LABEL[group], points, exercises: [...exs] })
  }
  return out.sort((a, b) => a.label.localeCompare(b.label))
}

/**
 * "Todas" overview: SECOND-LEVEL CHAINED index over the group indices, the exact mirror of
 * buildGroupIndices one level up (recalibration fix S4). A debuting group has no level at `prev`,
 * so it does not vote on its entry step (zero splice: the general no longer drops when a new group
 * starts, the 2026-08-01 incident). A group with no real event within STALE_DAYS of the step date
 * leaves the basket. Group ratios arrive already clamped; `provisional`/`lowConf` are inherited
 * from the groups' REAL points on the step date (no re-clamp here: mean of |ln| <= cap is <= cap).
 */
export function buildOverview(groups: GroupIndex[]): IndexPoint[] {
  const dates = [...new Set(groups.flatMap((g) => g.points.map((p) => p.date)))].sort()
  if (dates.length === 0) return []
  const ts = dates.map((d) => parseDay(d).getTime())
  const firstLowConf = groups.some((g) => g.points.some((p) => p.date === dates[0] && p.lowConf))
  const out: IndexPoint[] = [{ date: dates[0], t: ts[0], value: 100, obs: 0, provisional: false, lowConf: firstLowConf }]
  const cursors = mkCursors(groups, dates[0]) // mismo cursor monotono que un nivel abajo
  let level = 100
  for (let i = 1; i < dates.length; i++) {
    const date = dates[i]
    let sumLn = 0, n = 0, provisional = false, lowConf = false
    for (let m = 0; m < groups.length; m++) {
      const pts = groups[m].points
      const iPrev = cursors.at[m]
      cursors.advance(m, date)
      const iCur = cursors.at[m]
      if (iPrev < 0) continue // grupo que aun no debuta: no vota en su paso de entrada
      if (daysBetween(ts[i], pts[iPrev].t) > STALE_DAYS) continue // stale group: out of the basket
      const lp = pts[iPrev].value, lc = pts[iCur].value
      if (lp > 0 && lc > 0) {
        sumLn += Math.log(lc / lp); n++
        const real = pts[iCur].date === date ? pts[iCur] : undefined // evento REAL del grupo ese dia
        if (real?.provisional) provisional = true
        if (real?.lowConf) lowConf = true
      }
    }
    level *= n > 0 ? Math.exp(sumLn / n) : 1
    out.push({ date, t: ts[i], value: level, obs: n, provisional, lowConf })
  }
  return out
}

/** Rebase an exercise's raw series to an index (100 = its base) for the detail lines. */
export function rebased(s: ExerciseSeries): IndexPoint[] {
  return s.points.map((p) => ({ date: p.date, t: p.t, value: (p.value / s.base) * 100 }))
}

// ---------- honesty gate ----------

export type Trend = 'insuficiente' | 'mejorando' | 'estable' | 'bajando'
export interface TrendInfo {
  trend: Trend
  deltaPct: number
  nPoints: number
  windowDays?: number      // present ONLY when the windowed Theil-Sen read applied
  basketChanged?: boolean  // a basket event (debut/retirement) falls inside the read window
}

/**
 * Honest trend read of an index series. With < MIN_TREND_POINTS sessions we refuse to call a trend
 * ("insuficiente"). With enough points inside the TREND_WINDOW_DAYS window (anchored at the LAST
 * data point, never the wall clock), the direction comes from a Theil-Sen slope in log space
 * (median of pairwise slopes: one wild point cannot fake a trend, Sen 1968) applied over the
 * window's span; otherwise it falls back to the v1 whole-series endpoint read. A direction word is
 * emitted ONLY if the net change exceeds the ±noise gate; inside the gate it's "estable".
 */
export function trendOf(points: IndexPoint[], basketEventDates: readonly string[] = []): TrendInfo {
  const n = points.length
  if (n < MIN_TREND_POINTS) return { trend: 'insuficiente', deltaPct: 0, nPoints: n }
  const last = points[n - 1]
  const win = points.filter((p) => diffDays(last.date, p.date) <= TREND_WINDOW_DAYS)
  if (win.length < MIN_TREND_POINTS) {
    // Fallback: exact v1 behavior (whole series, endpoint ratio); same shape as v1 EXCEPT that
    // basketChanged still reports (Fase 4: the asterisk must not vanish exactly where the whole
    // history, basket changes included, is being read). windowDays stays absent: it is the only
    // discriminator of which branch ran.
    const delta = points[n - 1].value / points[0].value - 1
    const trend: Trend = Math.abs(delta) <= TREND_GATE ? 'estable' : delta > 0 ? 'mejorando' : 'bajando'
    const basketChanged = basketEventDates.some((d) => d >= points[0].date && d <= last.date)
    return { trend, deltaPct: delta * 100, nPoints: n, basketChanged }
  }
  const t0 = parseDay(win[0].date).getTime()
  // Whole-day distances (rounded like diffDays): parseDay yields LOCAL midnights, so a DST jump
  // would otherwise make dt a fraction and skew every pairwise slope (Fase 4).
  const days = (p: IndexPoint) => Math.round((p.t - t0) / 86400000)
  const slopes: number[] = []
  for (let i = 0; i < win.length; i++) {
    for (let j = i + 1; j < win.length; j++) {
      const dt = days(win[j]) - days(win[i])
      if (dt > 0) slopes.push((Math.log(win[j].value) - Math.log(win[i].value)) / dt)
    }
  }
  const b = median(slopes)
  const span = days(win[win.length - 1])
  const delta = Math.exp(b * span) - 1
  const trend: Trend = Math.abs(delta) <= TREND_GATE ? 'estable' : delta > 0 ? 'mejorando' : 'bajando'
  const basketChanged = basketEventDates.some((d) => d >= win[0].date && d <= last.date)
  return { trend, deltaPct: delta * 100, nPoints: win.length, windowDays: TREND_WINDOW_DAYS, basketChanged }
}

/** Convenience: everything the chart needs, computed from the store's days. */
export interface StrengthModel { groups: GroupIndex[]; overview: IndexPoint[]; series: ExerciseSeries[] }
export function buildStrengthModel(days: Record<string, DayState>): StrengthModel {
  const series = buildExerciseSeries(days)
  const groups = buildGroupIndices(series)
  const overview = buildOverview(groups)
  return { groups, overview, series }
}

// ---------- per-exercise TOP-SET history index (for the Sesión target resolver) ----------
//
// buildExerciseSeries above discards the raw w/r/rir (it keeps only the e1RM `value`), but the
// three-tier target resolver needs the actual weight, reps and RIR of the last top set (Tier 1
// double progression) and the top-set e1RM of a RELATED lift (Tier 2 estimate). This index shares
// resolveSets/rirHat with the chart builder (ONE resolution code path) but retains raw values and
// keeps ASSISTANCE lifts (with their INVERTED best-set comparator). TopSet.rir stays RAW: an
// imputed effort must never display as if the user logged it (fmtLast in target.ts).

/** The heaviest completed set of ONE exercise on ONE day (raw values kept, plus its e1RM). */
export interface TopSet {
  date: string        // local ISO day the set was logged
  w?: number          // working weight (kg); undefined only for reps-only lifts
  r: number           // reps
  rir?: number        // reps in reserve, if logged (RAW: never the imputed value)
  e1rm?: number       // Epley RIR-adjusted e1RM (kg, imputation applied); undefined for reps-only lifts
  repsOnly: boolean   // true for bodyweight / anti-rotation lifts (no meaningful load)
}

/** Per exercise NAME (canonical), its top set on each logged day, ascending by date. */
export interface HistoryIndex {
  byName: Map<string, TopSet[]>
}

/**
 * Build the per-exercise top-set history index from all logged days. Weighted lifts select the day's
 * top like the chart (v0 key over the reliable pool, else global); ASSISTANCE lifts keep the
 * inverted comparator (the BEST set is the LEAST-assisted one: min kg, tie -> max reps; v0 there
 * would crown the WORST set, audit MUST_FIX); reps-only lifts keep their reps.
 */
export function buildHistoryIndex(days: Record<string, DayState>): HistoryIndex {
  const sets = resolveSets(days)
  const rirHat = buildRirHat(sets)
  interface HAcc { best?: ResolvedSet & { v0: number }; bestRel?: ResolvedSet & { v0: number } }
  const acc = new Map<string, Map<string, HAcc>>() // name -> date -> candidates

  for (const s of sets) {
    if (!s.repsOnly && s.w == null) continue
    let byDate = acc.get(s.name)
    if (!byDate) { byDate = new Map(); acc.set(s.name, byDate) }
    const cur = byDate.get(s.date) ?? {}
    if (s.repsOnly) {
      if (!cur.best || s.r > cur.best.r) cur.best = { ...s, v0: 0 }
    } else if (s.assist) {
      // Inverted: least assistance wins (min kg, tie -> max reps).
      if (!cur.best || (s.w! < cur.best.w!) || (s.w === cur.best.w && s.r > cur.best.r)) cur.best = { ...s, v0: 0 }
    } else {
      const cand = { ...s, v0: e1rm(s.w!, s.r, 0) }
      // Same eligibility-by-OBSERVED-reps and tie-break rules as the chart builder (Fase 4).
      if (s.r <= REPS_CONF_MAX && better(cand, cur.bestRel)) cur.bestRel = cand
      if (better(cand, cur.best)) cur.best = cand
    }
    byDate.set(s.date, cur)
  }

  const byName = new Map<string, TopSet[]>()
  for (const [name, byDate] of acc) {
    const pts: TopSet[] = [...byDate.entries()]
      .sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([date, c]) => {
        const chosen = c.bestRel ?? c.best!
        const repsOnly = chosen.repsOnly
        const ei = repsOnly ? undefined : e1rm(chosen.w!, chosen.r, rirEffOf(chosen, rirHat))
        return { date, w: chosen.w, r: chosen.r, rir: chosen.rir, e1rm: ei, repsOnly }
      })
    byName.set(name, pts)
  }
  return { byName }
}

/**
 * The most-recent logged top set of `name` STRICTLY BEFORE `beforeDateIso` (null if none). Strict
 * "<" so the live "today" view never counts an in-progress set as its own history, and a past view
 * resolves relative to the VIEWED date. The query name is canonized (MF24: a V1 plan name keeps
 * finding its continued series) with the raw name as second defense.
 */
export function lastLoggedSet(idx: HistoryIndex, name: string, beforeDateIso: string): TopSet | null {
  const pts = idx.byName.get(canonicalName(name)) ?? idx.byName.get(name)
  if (!pts) return null
  for (let i = pts.length - 1; i >= 0; i--) if (pts[i].date < beforeDateIso) return pts[i]
  return null
}
